import { useMemo, useState } from "react"
import { Container } from "reactstrap"
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import Breadcrumb from "Components/organisms/breadcrumb"
import Tabs from "Components/molecule/tabs"
import { ANALYTICS_DAILY, ANALYTICS_KPIS, ANALYTICS_PAGES, ANALYTICS_USERS } from "data/analytics-dummy"
import { formatDate } from "utils/tools"

const TABS = ["Overview", "Pages", "Modules", "Users"]

const fmtTime = (sec: number) => `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, "0")}s`

// Analytics insights for IbexIQ usage. Shows sample data until it is connected to Google Analytics.
const AnalyticsPage = () => {
    const [activeTab, setActiveTab] = useState(TABS[0])

    const modules = useMemo(() => {
        const map = new Map<string, { views: number, users: number }>()
        ANALYTICS_PAGES.forEach(p => {
            const m = map.get(p.module) ?? { views: 0, users: 0 }
            map.set(p.module, { views: m.views + p.views, users: m.users + p.users })
        })
        return Array.from(map.entries()).sort((a, b) => b[1].views - a[1].views)
    }, [])
    const maxModuleViews = modules[0]?.[1].views ?? 1

    const overview = <div className="d-flex flex-column gap-3">
        <div className="analytics-kpis">
            {ANALYTICS_KPIS.map(k => (
                <div key={k.label} className="analytics-kpi">
                    <div className="text-muted small">{k.label}</div>
                    <div className="analytics-kpi__value">{k.value}</div>
                    <div className={`small ${k.change >= 0 ? "text-success" : "text-danger"}`}>
                        {k.change >= 0 ? "▲" : "▼"} {Math.abs(k.change).toFixed(1)}% vs previous 30 days
                    </div>
                </div>
            ))}
        </div>
        <div>
            <h6>Daily users and sessions (last 30 days)</h6>
            <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                    <LineChart data={ANALYTICS_DAILY} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bs-border-color)" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="users" name="Users" stroke="var(--bs-primary)" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sessions" name="Sessions" stroke="var(--bs-success)" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    </div>

    const pages = <table className="table table-sm align-middle mb-0">
        <thead><tr><th>Page</th><th>Module</th><th className="text-end">Views</th><th className="text-end">Users</th><th className="text-end">Avg. Time</th></tr></thead>
        <tbody>
            {ANALYTICS_PAGES.map(p => (
                <tr key={p.page}>
                    <td>{p.page}</td><td>{p.module}</td>
                    <td className="text-end">{p.views.toLocaleString("en-CA")}</td>
                    <td className="text-end">{p.users}</td>
                    <td className="text-end">{fmtTime(p.avgTimeSec)}</td>
                </tr>
            ))}
        </tbody>
    </table>

    const modulesView = <div className="d-flex flex-column gap-2">
        {modules.map(([name, m]) => (
            <div key={name}>
                <div className="d-flex justify-content-between small"><span>{name}</span><span>{m.views.toLocaleString("en-CA")} views</span></div>
                <div className="analytics-bar"><div style={{ width: `${(m.views / maxModuleViews) * 100}%` }} /></div>
            </div>
        ))}
    </div>

    const users = <table className="table table-sm align-middle mb-0">
        <thead><tr><th>User</th><th>Department</th><th className="text-end">Sessions</th><th>Last Seen</th></tr></thead>
        <tbody>
            {ANALYTICS_USERS.map(u => (
                <tr key={u.name}><td>{u.name}</td><td>{u.department}</td><td className="text-end">{u.sessions}</td><td>{formatDate(u.lastSeen)}</td></tr>
            ))}
        </tbody>
    </table>

    return <div className="page-content">
        <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <Breadcrumb title="Admin" breadcrumbItem="Analytics" />
            <div className="alert alert-info mb-0">
                Sample data. This page will show live usage once it is connected to Google Analytics.
            </div>
            <div className="section-container">
                <Tabs
                    headers={TABS}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    tabs={{ Overview: overview, Pages: pages, Modules: modulesView, Users: users }}
                />
            </div>
        </Container>
    </div>
}

export default AnalyticsPage
